<script lang='ts'>

    /* IMPORTS --------------------------------------------------------------------------------- */

    // STYLING
    import 'tailwindcss/tailwind.css';

    // CASSIOPEIA
    import {
        AddressForm,
        AddressItem,
        AddressList,
        Button,
        OutputAddress,
        SelectAddress,
    } from '@smatch/cassiopeia-web-components';

    // UTILITIES
    import { createEventDispatcher } from 'svelte';

    // TYPES
    import type { Address, FormComponentLegacy } from 'src/app/types';

    interface CustomSaveEvent extends CustomEvent {
        detail: Address;
    }

    /* PROPS ----------------------------------------------------------------------------------- */

    export let key: string | undefined;
    export let organizationKey: string;
    export let type: Address['type'];

    /* INTERNALS ------------------------------------------------------------------------------- */

    let Form: FormComponentLegacy | null = null;
    let showEditForm = false;
    let showCreateForm = false;

    const dispatch = createEventDispatcher();

    const showDropdown = (): void => {
        key = undefined;
    };

    const toggleEditFormVisibility = (): void => {
        showEditForm = !showEditForm;
    };

    const toggleCreateFormVisibility = (): void => {
        showCreateForm = !showCreateForm;
    };

    const onSave = (event: CustomSaveEvent): void => {
        showEditForm = false;
        showCreateForm = false;
        dispatch('save', event.detail);
    };

    /* INTERFACE ------------------------------------------------------------------------------ */

    export const submit = async (): Promise<void> => {
        await (Form as FormComponentLegacy).submit();
    };

    export const isValid = (): boolean => (Form as FormComponentLegacy).isValid();

</script>

{#if showCreateForm }

    <div class = 's8-cancel'>

        <Button
            on:click = { toggleCreateFormVisibility }
            color = 'neutral'
            size = 'xs'
            label = 'Cancel'
        />

    </div>

    <AddressForm
        action = 'create'
        address = { { type } }
        bind:this = { Form }
        on:cancel
        on:save = { onSave }
        showCancel
        { organizationKey }
    />

{:else if key === undefined }

    <AddressList
        parameters = { { addressType: type, organizationKey, states: [ 'created', 'approved' ] } }
        let:addresses
    >

        <div class = 's8-list'>

            <SelectAddress bind:key { addresses } label = { null }/>

            <span>or</span>

            <Button
                on:click = { toggleCreateFormVisibility }
                color = 'dark'
                size = 'sm'
                label = 'Add Address'
            />

        </div>

        <svelte:fragment slot = 'empty'>
            <AddressForm
                action = 'create'
                address = { { type } }
                bind:this = { Form }
                on:cancel
                on:save = { onSave }
                showCancel
                { organizationKey }
            />
        </svelte:fragment>

    </AddressList>

{:else}

    <AddressItem { key } let:address>

        <!-- TODO: Fix slot types -->
        <!-- eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access -->

        {#if showEditForm || address._.state === 'created'}

            <div class = 's8-cancel'>

                <Button
                    on:click = { toggleEditFormVisibility }
                    color = 'neutral'
                    size = 'xs'
                    label = 'Cancel'
                />

            </div>

            <AddressForm
                { address }
                action = 'update'
                bind:this = { Form }
                on:cancel = { toggleEditFormVisibility }
                on:save = { onSave }
                showCancel
                { organizationKey }
            />

        {:else}

            <div class = 's8-address-item'>

            <OutputAddress { address } mode = 'full'/>

                <div class = 's8-item-toggles'>

                    <Button
                        on:click = { toggleEditFormVisibility }
                        color = 'primary'
                        size = 'xxs'
                        label = 'Edit'
                    />

                    <Button
                        on:click = { toggleCreateFormVisibility }
                        color = 'dark'
                        size = 'xxs'
                        label = 'New Address'
                    />

                    <Button
                        on:click = { showDropdown }
                        color = 'dark'
                        size = 'xxs'
                        label = 'Address List'
                    />

                </div>

            </div>

        {/if}

        <!-- eslint-enable -->

    </AddressItem>

{/if}

<style lang="postcss">

    div.s8-list {
        display: flex;
        gap: 10px;
        align-items: baseline;

        span {
            width: 30px;
            text-align: center;
            color: rgba(39,39,17,0.4);
        }
    }

    div.s8-item-toggles {
        margin-top: 10px;
    }

    div.s8-cancel {
        position: relative;
        padding-bottom: 10px;

        :global(button) {
            position: absolute;
            right: 0px;
            top: -30px;
        }
    }

</style>